import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';
import useAdminDialog from '../components/AdminDialog';

const STATUS_BADGE = { pending: 'badge-blue', accepted: 'badge-green', rejected: 'badge-gray' };

export default function Bids() {
  const { copy } = useAdminLanguage();
  const bidsCopy = copy.bids;
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const { confirmAction, dialog } = useAdminDialog();

  const fetchBids = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/bids', { params: { status: filter, keyword } })
      .then(res => setBids(res.data.bids || []))
      .catch(err => setError(err.response?.data?.error || bidsCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [filter, keyword, bidsCopy.errors.loadFailed]);

  useEffect(() => { fetchBids(); }, [fetchBids]);

  const filtered = bids.filter(b => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (keyword.trim()) {
      const q = keyword.trim().toLowerCase();
      return (b.task_title || '').toLowerCase().includes(q) || (b.bidder_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const handleAccept = async (id) => {
    if (!await confirmAction({
      title: bidsCopy.confirm.accept.title,
      message: bidsCopy.confirm.accept.message,
    })) return;
    setError('');
    try {
      await api.post(`/admin/bids/${id}/accept`);
      fetchBids();
    } catch (err) {
      setError(err.response?.data?.error || bidsCopy.errors.acceptFailed);
    }
  };
  const handleReject = async (id) => {
    if (!await confirmAction({
      title: bidsCopy.confirm.reject.title,
      message: bidsCopy.confirm.reject.message,
    })) return;
    setError('');
    try {
      await api.post(`/admin/bids/${id}/reject`);
      fetchBids();
    } catch (err) {
      setError(err.response?.data?.error || bidsCopy.errors.rejectFailed);
    }
  };
  const handleDelete = async (id) => {
    if (!await confirmAction({
      title: bidsCopy.confirm.delete.title,
      message: bidsCopy.confirm.delete.message,
      variant: 'danger',
    })) return;
    setError('');
    try {
      await api.delete(`/admin/bids/${id}`);
      fetchBids();
    } catch (err) {
      setError(err.response?.data?.error || bidsCopy.errors.deleteFailed);
    }
  };

  const stats = {
    total: bids.length,
    pending: bids.filter(b => b.status === 'pending').length,
    accepted: bids.filter(b => b.status === 'accepted').length,
    rejected: bids.filter(b => b.status === 'rejected').length,
  };

  return (
    <div>
      {dialog}
      <div className="page-title">{bidsCopy.title}</div>
      {error && <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>{error}</div>}

      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="card stat-card"><div className="stat-label">{bidsCopy.stats.total}</div><div className="stat-value">{stats.total}</div></div>
        <div className="card stat-card"><div className="stat-label">{bidsCopy.stats.pending}</div><div className="stat-value" style={{ color: '#3b82f6' }}>{stats.pending}</div></div>
        <div className="card stat-card"><div className="stat-label">{bidsCopy.stats.accepted}</div><div className="stat-value" style={{ color: '#1dbf73' }}>{stats.accepted}</div></div>
        <div className="card stat-card"><div className="stat-label">{bidsCopy.stats.rejected}</div><div className="stat-value" style={{ color: '#9ca3af' }}>{stats.rejected}</div></div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div className="search-input">
            <input placeholder={bidsCopy.searchPlaceholder} value={keyword} onChange={e => setKeyword(e.target.value)} />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <option value="all">{bidsCopy.allStatus}</option>
            <option value="pending">{bidsCopy.status.pending}</option>
            <option value="accepted">{bidsCopy.status.accepted}</option>
            <option value="rejected">{bidsCopy.status.rejected}</option>
          </select>
          <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center', marginLeft: 'auto' }}>{bidsCopy.showing(filtered.length, bids.length)}</span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{bidsCopy.columns.id}</th><th>{bidsCopy.columns.task}</th><th>{bidsCopy.columns.bidder}</th><th>{bidsCopy.columns.price}</th><th>{bidsCopy.columns.message}</th><th>{bidsCopy.columns.status}</th><th>{bidsCopy.columns.createdAt}</th><th>{bidsCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{bidsCopy.loading}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{bidsCopy.empty}</td></tr>
              ) : filtered.map(b => (
                <tr key={b.id}>
                  <td>{b.id}</td>
                  <td style={{ fontWeight: 600 }}>{b.task_title || '-'}</td>
                  <td>{b.bidder_name || '-'}</td>
                  <td style={{ color: '#1dbf73', fontWeight: 700 }}>¥{Number(b.price || 0).toLocaleString()}</td>
                  <td style={{ color: '#555', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.message}</td>
                  <td><span className={`badge ${STATUS_BADGE[b.status] || 'badge-gray'}`}>{bidsCopy.status[b.status] || b.status}</span></td>
                  <td style={{ color: '#6b7280' }}>{new Date(b.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {b.status === 'pending' && (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={() => handleAccept(b.id)}>{bidsCopy.actions.accept}</button>
                          <button className="btn btn-sm btn-outline" onClick={() => handleReject(b.id)}>{bidsCopy.actions.reject}</button>
                        </>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(b.id)}>{bidsCopy.actions.delete}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
